import React, { useState, useContext, useEffect, useRef } from "react";
import { submitFormData } from "../../../../services/APIRequest";
import { serializeForm } from "../../../../helpers/FormSerializer";
import { OrganizationContext } from 'context/OrganizationContext';
import { useParseHTML } from "hooks/useParseHTML";
import styles from "../DynamicForm.module.scss";
import { renderFormItem } from "../utils/formHelpers";

import RenderItemsByType from "./RenderItemsByType";
import ContactInformationSection from "./ContactInformationSection";
import SelectionCounter from "./SelectionCounter";
import Button from "components/elements/Button/Button"
import { LoggerContext } from "context/LoggerContext";
import FixedContainer from "components/elements/FixedContainer/FixedContainer";
import confetti from "utils/confetti";

/**
 * Simple form without steps
 */
const SimpleForm = ({ data }) => {
    const { logDebug } = useContext(LoggerContext);
    const organization = useContext(OrganizationContext);
    const [selectionCounts, setSelectionCounts] = useState({}); // Tracks selection counts for validation purposes
    const [showValidationErrors, setShowValidationErrors] = useState({});
    const [radioValidation, setRadioValidation] = useState({});
    const [maxCountReached, setMaxCountReached] = useState({});
    const [isSubmitted, setIsSubmitted] = useState(false);
    const formRef = useRef(null);

    // Refs for input elements
    const inputRefs = useRef({});

    // Handler for selection changes in SelectionCounter, similar to MultiStepForm
    const handleSelectionChange = (data) => {
        // Update the count
        setSelectionCounts(prev => ({
            ...prev,
            [data.stepNumber]: {
                selectedCount: data.selectedCount,
                minCount: data.minCount,
                maxCount: data.maxCount,
                isValid: data.isValid
            }
        }));
        
        // Save the max_count status
        if (data.maxReached !== undefined) {
            setMaxCountReached(prev => ({
                ...prev,
                [data.stepNumber]: data.maxReached
            }));
        }

        // NEW: Remove ValidationError when error is corrected
        if (data.clearValidationError) {
            setShowValidationErrors(prev => ({
                ...prev,
                [data.stepNumber]: false
            }));
            
            // Find the step and remove the error class
            const stepElement = document.querySelector(`[data-step="${data.stepNumber}"]`);
            if (stepElement) {
                stepElement.classList.remove('step-has-error');
            }
        }

        // If it's a radio group, update radioValidation as well
        if (data.radioGroup) {
            setRadioValidation(prev => ({
                ...prev,
                [data.radioGroup]: {
                    isValid: data.isValid
                }
            }));
        }
    };

    // Verbesserte Funktion für die Behandlung von Input-Änderungen
    const handleInputChange = (e) => {
        const input = e.target;
        
        if (input.validity.valid) {
            input.classList.remove('is-invalid');
            
            // Für Kontaktdaten prüfen, ob es noch weitere Fehler gibt
            const contactSection = input.closest('.contact-information');
            if (contactSection) {
                const container = contactSection.closest('[data-step]') || contactSection;
                
                // Jetzt explizit prüfen, ob ALLE erforderlichen Felder in der Kontaktsektion ausgefüllt sind
                const allRequiredInputs = container.querySelectorAll('input[required], select[required], textarea[required]');
                let allValid = true;
                
                allRequiredInputs.forEach(requiredInput => {
                    // Für Checkboxen und Radio-Buttons prüfen, ob sie markiert sind
                    if (requiredInput.type === 'checkbox' || requiredInput.type === 'radio') {
                        if (!requiredInput.checked) {
                            allValid = false;
                        }
                    } 
                    // Für alle anderen Input-Typen prüfen, ob sie einen Wert haben
                    else if (!requiredInput.value) {
                        allValid = false;
                    }
                });
                
                // Wenn ALLE erforderlichen Felder gültig sind, entferne die Fehlermarkierung
                if (allValid) {
                    container.classList.remove('step-has-error');
                }
            } else {
                // Für normale Felder das bisherige Verhalten beibehalten
                const formGroup = input.closest('.form-group, .mb-3');
                if (formGroup) {
                    formGroup.classList.remove('has-validation-error');
                }
                
                const stepElement = input.closest('[data-step]');
                if (stepElement) {
                    // Bei normalen Feldern prüfen, ob noch andere Fehler im Step existieren
                    const otherErrors = stepElement.querySelectorAll('.is-invalid, .has-validation-error');
                    if (otherErrors.length === 0) {
                        stepElement.classList.remove('step-has-error');
                    }
                }
            }
        }
    };

    // Enhanced validation function that validates all fields, even if they've never been touched
    const validateForm = () => {
        let isValid = true;
        const errorElements = [];
        const errorSteps = new Set();

        // 1. First reset all fields that were previously marked as invalid
        const allInvalidFields = document.querySelectorAll('.is-invalid');
        allInvalidFields.forEach(field => field.classList.remove('is-invalid'));
        
        const allErrorContainers = document.querySelectorAll('.step-has-error, .has-validation-error');
        allErrorContainers.forEach(container => {
            container.classList.remove('step-has-error');
            container.classList.remove('has-validation-error');
        });

        // 2. Check all steps for min_count requirements
        if (data.steps) {
            const activeSteps = data.steps.filter(step => step.active === 1);
            
            activeSteps.forEach((step, index) => {
                const stepNumber = index + 1;
                const stepElement = document.querySelector(`[data-step="${stepNumber}"]`);
                if (!stepElement) return;
                
                // If min_count is set, count manually
                if (step.min_count && step.min_count > 0) {
                    // Count all selections including checkboxes, radio groups, and filled text inputs
                    const checkboxes = stepElement.querySelectorAll('input[type="checkbox"]:checked').length;
                    
                    const radioGroups = new Set();
                    stepElement.querySelectorAll('input[type="radio"]:checked').forEach(radio => {
                        radioGroups.add(radio.name);
                    });
                    
                    // Count text inputs that have values as well
                    const textInputs = stepElement.querySelectorAll('input[type="text"], input[type="email"], input[type="tel"], textarea');
                    const filledTextInputsCount = Array.from(textInputs).filter(input => input.value.trim() !== '').length;
                    
                    const selectedCount = checkboxes + radioGroups.size + filledTextInputsCount;
                    
                    if (selectedCount < step.min_count) {
                        isValid = false;
                        setShowValidationErrors(prev => ({
                            ...prev,
                            [stepNumber]: true
                        }));
                        
                        errorSteps.add(stepNumber);
                        
                        errorElements.push({
                            element: stepElement,
                            top: stepElement.getBoundingClientRect().top + window.pageYOffset
                        });
                    }
                }
                
                // Required-Felder prüfen
                const requiredInputs = stepElement.querySelectorAll('input[required], select[required], textarea[required]');
                requiredInputs.forEach(input => {
                    // Auch nie berührte Felder als ungültig markieren
                    if (input.type === 'checkbox' || input.type === 'radio') {
                        if (!input.checked) {
                            input.classList.add('is-invalid');
                            isValid = false;
                            errorSteps.add(stepNumber);
                            
                            errorElements.push({
                                element: input,
                                top: input.getBoundingClientRect().top + window.pageYOffset
                            });
                        }
                    } else {
                        if (!input.value) {
                            input.classList.add('is-invalid');
                            isValid = false;
                            errorSteps.add(stepNumber);
                            
                            // Für die Parent-Container - aber nicht in Kontaktdaten
                            const isInContactSection = input.closest('.contact-information');
                            const formGroup = input.closest('.form-group, .mb-3');
                            
                            if (formGroup && !isInContactSection) {
                                formGroup.classList.add('has-validation-error');
                            }
                            
                            errorElements.push({
                                element: input,
                                top: input.getBoundingClientRect().top + window.pageYOffset
                            });
                        }
                    }
                });
            });
        }

        // 3. Check contact data section if present
        const contactSection = document.querySelector('.contact-information');
        if (contactSection) {
            const requiredInputs = contactSection.querySelectorAll('input[required], select[required], textarea[required]');
            let hasErrors = false;
            
            requiredInputs.forEach(input => {
                if (!input.value) {
                    input.classList.add('is-invalid');
                    isValid = false;
                    hasErrors = true;
                    
                    errorElements.push({
                        element: input,
                        top: input.getBoundingClientRect().top + window.pageYOffset
                    });
                }
            });
            
            // Finde den Container der Kontaktsektion und markiere ihn bei Fehlern
            if (hasErrors) {
                const contactContainer = contactSection.closest('[data-step]');
                if (contactContainer) {
                    contactContainer.classList.add('step-has-error');
                    
                    // Falls kein data-step, dann direkt die Kontaktsektion
                    const stepNumber = contactContainer.getAttribute('data-step');
                    if (stepNumber) {
                        errorSteps.add(parseInt(stepNumber, 10));
                    }
                } else {
                    contactSection.classList.add('step-has-error');
                }
            }
        }
        
        // 4. Policy-Checkbox prüfen
        const policyCheckbox = document.getElementById('privacyCheckboxSimple');
        if (policyCheckbox && !policyCheckbox.checked) {
            policyCheckbox.classList.add('is-invalid');
            isValid = false;
            
            errorElements.push({
                element: policyCheckbox,
                top: policyCheckbox.getBoundingClientRect().top + window.pageYOffset
            });
        }

        // Set radio validation separately
        const radioGroups = formRef.current?.querySelectorAll('[data-radio-group]'); // Hier formRef.current statt formElement
        if (radioGroups) {
            radioGroups.forEach(group => {
                const name = group.getAttribute('data-radio-group');
                const radios = group.querySelectorAll('input[type="radio"]');
                const isRequired = Array.from(radios).some(r => r.required);
                const isChecked = Array.from(radios).some(r => r.checked);
                
                if (isRequired && !isChecked) {
                    isValid = false;
                    setRadioValidation(prev => ({
                        ...prev, 
                        [name]: { isValid: false }
                    }));
                    
                    // Füge auch zur Fehler-Liste hinzu
                    errorElements.push({
                        element: group,
                        top: group.getBoundingClientRect().top + window.pageYOffset
                    });
                }
            });
        }

        // 5. Markiere fehlerhafte Schritte im UI
        errorSteps.forEach(stepNumber => {
            const stepElement = document.querySelector(`[data-step="${stepNumber}"]`);
            if (stepElement) {
                stepElement.classList.add('step-has-error');
            }
        });

        // 6. Sortiere und scrolle zum ersten Fehler
        errorElements.sort((a, b) => a.top - b.top);

        // 7. Scroll to the first error
        if (errorElements.length > 0) {
            const firstErrorElement = errorElements[0].element;
            
            setTimeout(() => {
                firstErrorElement.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'center' 
                });
                
                if (firstErrorElement.focus && typeof firstErrorElement.focus === 'function') {
                    firstErrorElement.focus();
                    
                    // Special handling for select elements
                    if (firstErrorElement.tagName === 'SELECT') {
                        try {
                            const selectControl = firstErrorElement.closest('.form-group, .mb-3')?.querySelector('.select__control');
                            if (selectControl) selectControl.click();
                        } catch (e) {
                            logDebug('Could not focus select element', e);
                        }
                    }
                }
            }, 100);
        }

        return isValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const formElement = e.target;
        
        // Erst unsere eigene Validierung durchführen und dabei zum ersten Fehler scrollen
        const isValid = validateForm();
        
        if (!isValid) {
            // Validierungsmeldungen wurden bereits in validateForm gesetzt
            // Scrollen zum ersten Fehler wurde ebenfalls in validateForm abgehandelt
            return; // Submit-Prozess abbrechen
        } 
        
        // Wenn hier, dann hat die Validierung bestanden - Formular absenden
        try {
            setIsSubmitted(true); // Status setzen, dass Formular abgeschickt wurde
            const formData = serializeForm(formElement, data);
            const suffix = data.lead_suffix || 'ticket/leads/public';
            
            await submitFormData(suffix, formData, organization?.otk);
            
            alert("Formular erfolgreich abgeschickt!");

            // Starte Confetti mit Timeout von 2000ms (2 Sekunden)
            const confettiController = confetti();
            confettiController.start(2000);
            
            // Seite nach dem Confetti neu laden
            setTimeout(() => {
                window.location.reload();
            }, 2000);
        } catch (error) {
            setIsSubmitted(false); // Bei Fehler zurücksetzen
            alert("Fehler beim Absenden: " + error.message);
        }
    };

    // Funktion zum Speichern von Refs für InputElemente
    const setInputRef = (name, ref) => {
        if (ref) {
            inputRefs.current[name] = ref;
        }
    };

    const leadContactType = data.lead_contact || 1; // Default auf 1 (keine Kontaktdaten) falls nicht angegeben

    // Check if contact details should be shown first
    const contactDetailsFirst = data.contact_details_first === 1;
    const showContactInformation = leadContactType > 1;

    // In der SimpleForm-Komponente müssen wir den enableAnonymousOption Parameter weiterreichen
    // Hierfür füge ich einen neuen Parameter aus den Daten hinzu
    const enableAnonymousOption = data.enable_anonymous_option === 1;

    // Initialisiere die aktive Klasse für vorausgewählte Checkboxen und Radios
    useEffect(() => {
        // Diese Initialisierung sollte nur EINMAL laufen, nicht bei jeder Änderung
        const initializeActiveStatus = () => {
            const tiles = document.querySelectorAll(`.${styles.tile}`);
            tiles.forEach(tile => {
                const input = tile.querySelector('input[type="checkbox"], input[type="radio"]');
                if (input && input.checked) {
                    tile.classList.add(styles.active);
                }
            });
        };
        
        // Führe die Initialisierung nach einem kurzen Delay aus
        setTimeout(initializeActiveStatus, 100);
    }, []); // Leeres Dependency-Array hinzufügen, damit es nur einmal ausgeführt wird

    // Neuer Handler für Keydown-Events
    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
        }
    };

    useEffect(() => {
        // Event-Listener bei Komponenten-Mount hinzufügen
        const form = formRef.current;
        if (form) {
            form.addEventListener('keydown', handleKeyDown);
        }

        // Cleanup bei Komponenten-Unmount
        return () => {
            if (form) {
                form.removeEventListener('keydown', handleKeyDown);
            }
        };
    }, []);

    // Log selection counts for debugging when they change
    useEffect(() => {
        // logDebug('Form selection counts updated:', selectionCounts);
    }, [selectionCounts, maxCountReached, isSubmitted, logDebug]);

    // Event-Listener für Input-Änderungen hinzufügen
    useEffect(() => {
        const form = formRef.current;
        if (form) {
            // Global listener für Input-Änderungen
            const handleGlobalInputChange = (e) => {
                if (e.target.tagName === 'INPUT' || e.target.tagName === 'SELECT' || e.target.tagName === 'TEXTAREA') {
                    handleInputChange(e);
                }
            };
            
            form.addEventListener('input', handleGlobalInputChange);
            form.addEventListener('change', handleGlobalInputChange);
            
            return () => {
                form.removeEventListener('input', handleGlobalInputChange);
                form.removeEventListener('change', handleGlobalInputChange);
            };
        }
    }, []);

    // Handler für Policy-Checkbox Änderungen
    const handlePolicyChange = (e) => {
        if (e.target.checked) {
            // Entferne die is-invalid Klasse
            e.target.classList.remove('is-invalid');
        }
    };

    return (
        <form
            ref={formRef}
            onSubmit={handleSubmit}
            className="needs-validation"
            noValidate
            onKeyDown={handleKeyDown} // Zusätzlicher Handler direkt am form-Element
        >
            {/* Verstecktes event_id Feld auf Top-Level */}
            {/* <input
                type="hidden"
                name="event_id"
                ref={(ref) => setInputRef && setInputRef('event_id', ref)}
                value="462"
                data-serialize-in-top-level="true" 
                className="d-none"
            /> */}
            
            {/* Vergrößerter Abstand von mb-3 zu mb-5 */}
            <div className="mb-5">
                {/* Zusätzlicher Abstand unter der Überschrift */}
                {data.title && <h3 className="mb-4">{data.title}</h3>}
                {data.short_desc && <p className="mb-3">{data.short_desc}</p>}
                {data.desc && <div className="mb-4" dangerouslySetInnerHTML={{ __html: data.desc }} />}
            </div>
            
            {/* Contact details section at the beginning if configured */}
            {showContactInformation && contactDetailsFirst && (
                <div className="mb-5" data-step="contact">
                    <ContactInformationSection 
                        leadContactType={leadContactType} 
                        setInputRef={setInputRef}
                        enableAnonymousOption={enableAnonymousOption}
                    />
                </div>
            )}
            
            {data.steps
                ? data.steps
                        .filter((step) => step.active === 1)
                        .map((step, index) => (
                            <div
                                key={index}
                                className={`${styles.simpleFormSection} mb-5`}
                                data-step={index + 1}>
                                {/* Vergrößerter Abstand */}
                                <div className="mb-4">
                                    {/* Zusätzlicher Abstand unter der Überschrift */}
                                    <h5 className="mb-3">{step.title}</h5>
                                    {step.short_desc && <p className="mb-3">{step.short_desc}</p>}
                                    {step.desc && <div className="mb-4" dangerouslySetInnerHTML={{ __html: step.desc }} />}
                                </div>
                                
                                {/* Padding oben und unten für mehr Luftigkeit */}
                                <div className="py-2">
                                    <RenderItemsByType 
                                        step={step} 
                                        stepIndex={index} 
                                        itemIndex={index}
                                        onSelectionChange={handleSelectionChange}
                                        showValidationError={showValidationErrors[index + 1]}
                                        radioValidation={radioValidation}
                                    />
                                </div>
                            </div>
                        ))
                    : data.items && (
                        <div className="row justify-content-center mb-5">
                            <div className="col-md-6">
                                {/* Wenn data.items ein min_count oder max_count hat... */}
                                {(data.min_count > 0 || data.max_count < Infinity) ? (
                                    <SelectionCounter
                                        step={{
                                            stepNumber: 1,
                                            min_count: data.min_count || 0,
                                            max_count: data.max_count || Infinity
                                        }}
                                        onSelectionChange={handleSelectionChange}
                                        showValidationError={showValidationErrors[1]}
                                    >
                                        {/* Mehr Abstand innerhalb der Section */}
                                        <div className="py-3">
                                            {data.items.map((item, idx) => renderFormItem(item, idx))}
                                        </div>
                                    </SelectionCounter>
                                ) : (
                                    <div className="py-3">
                                        {data.items.map((item, idx) => renderFormItem(item, idx))}
                                    </div>
                                )}
                            </div>
                        </div>
                  )}
            
            {/* Contact details section at the end if not configured to be first */}
            {showContactInformation && !contactDetailsFirst && (
                <div className="mb-5" data-step="contact">
                    <ContactInformationSection 
                        leadContactType={leadContactType} 
                        setInputRef={setInputRef}
                        enableAnonymousOption={enableAnonymousOption}
                    />
                </div>
            )}

            {/* Viel mehr Abstand vor der Policy */}
            <div className="mb-5 mt-5 pt-3">
                <div className="form-check">
                    <input 
                        className="form-check-input" 
                        type="checkbox" 
                        id="privacyCheckboxSimple" 
                        required 
                        onChange={handlePolicyChange}
                    />
                    <label 
                        className="form-check-label text-xs" 
                        htmlFor="privacyCheckboxSimple" 
                        dangerouslySetInnerHTML={useParseHTML(data.policy_text || "Ich akzeptiere die Datenschutzbestimmungen")}
                    />
                </div>
            </div>
            <FixedContainer fixedOn="md" position="bottom" className="mt-4 pt-2" title={data.title}>
                <Button type="submit" variant="primary" size="lg" letterCase="uppercase">
                    Absenden
                </Button>
            </FixedContainer>
        </form>
    );
};

export default SimpleForm;
